import { AI_REPORT_FIGURE_TOKEN, stripAiReportFigureMarkers, type AiReport, type AiReportFigureId } from '@erp/shared';
import { AlertTriangle, ArrowLeft, Banknote, FileText, Landmark, Printer, RefreshCw, Sparkles, Trash2, TrendingUp, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, CopyButton, DirectionalIcon, EmptyState, MarkdownLite, StatCard, confirmDialog } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import { FIGURE_SCOPE_CSS, buildReportFigures } from './reportFigures.js';
import { StatusBadge, TYPE_ICON, useErrorText } from './reportMeta.js';
import { useAiReport, useDeleteAiReport, useRegenerateAiReport } from './useAiReports.js';

/**
 * The report itself. While the analyst is writing, the page shows a shimmer and
 * quietly re-polls; the KPI chips and the daily-sales chart are rendered
 * DETERMINISTICALLY from the stored snapshot — never parsed out of the prose —
 * so what the cards say is exactly what the generator was given.
 */
export function AiReportViewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('aiReports');
  const navigate = useNavigate();
  const { data: report, isLoading, isError } = useAiReport(id);
  const regenerate = useRegenerateAiReport(id ?? '');
  const del = useDeleteAiReport();
  const canManage = usePermission('ai_reports.manage');
  const canAiAdmin = usePermission('ai.admin');
  const errorText = useErrorText();

  // A deleted report's stale link 404s — that must read as "gone", not load forever.
  if (isError) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-8 w-8" aria-hidden />}
        title={t('aiReports.notFound.title', { defaultValue: 'Report not found' })}
        description={t('aiReports.notFound.hint', { defaultValue: 'It may have been deleted, or the link is stale.' })}
        action={(
          <Button type="button" variant="secondary" onClick={() => navigate('/reports/ai')}>
            {t('aiReports.title', { defaultValue: 'AI Reports' })}
          </Button>
        )}
      />
    );
  }
  if (isLoading || !report) {
    return <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>;
  }

  const Icon = TYPE_ICON[report.type];
  const denied = Object.values(report.snapshot.sections ?? {}).some(
    (s) => !!s && typeof s === 'object' && (s as { denied?: boolean }).denied === true,
  );

  // A rejected mutation (429 shared limiter, 422 while already running, 500)
  // must say so — a silently re-enabled button reads as "the feature is broken".
  const serverError = (): void => { toast.error(t('server', { ns: 'errors', defaultValue: 'Something went wrong. Please try again.' })); };

  const onRegenerate = async (): Promise<void> => {
    if (await confirmDialog({
      message: t('aiReports.view.regenerateHint', { defaultValue: 'Rewrites the prose over the same captured data' }),
      title: t('aiReports.view.regenerate', { defaultValue: 'Regenerate' }),
    })) {
      try { await regenerate.mutateAsync(); } catch { serverError(); }
    }
  };

  const onRetry = async (): Promise<void> => {
    try { await regenerate.mutateAsync(); } catch { serverError(); }
  };

  const onDelete = async (): Promise<void> => {
    if (await confirmDialog({
      message: t('aiReports.view.deleteConfirm', { defaultValue: 'Delete this report? This cannot be undone.' }),
      title: t('aiReports.view.delete', { defaultValue: 'Delete' }),
      tone: 'danger',
    })) {
      try {
        await del.mutateAsync(report.id);
        navigate('/reports/ai');
      } catch { serverError(); }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Print: only the report body survives on paper. Every existing print
          surface is a standalone route; this is the first window.print() INSIDE
          the app shell, whose fixed-height overflow-hidden ancestors would clip
          the output to one page — so they must be unclipped here too. The dark
          theme is attribute-driven ([data-theme="dark"]) and survives printing,
          so the light token set is forced explicitly — otherwise every label,
          tick and table prints as white ink on white paper. print-color-adjust
          keeps the figures' CSS-background bars (aging ramp, legend swatches),
          which browsers otherwise strip as "background graphics". */}
      <style>{`@media print {
        :root, [data-theme="dark"], .dark {
          --color-surface: #FFFFFF !important; --color-surface-muted: #F8F7F4 !important; --color-surface-sunk: #EFEEE9 !important;
          --color-border: #E0DED7 !important; --color-border-strong: #C2C0B7 !important;
          --color-text: #15171A !important; --color-text-muted: #54534D !important; --color-text-subtle: #73716A !important;
        }
        .erp-shell, .erp-shell .window, .erp-shell .app, .erp-shell .main, .erp-shell .scroll {
          height: auto !important; max-height: none !important; overflow: visible !important; position: static !important;
        }
        body * { visibility: hidden; }
        #ai-report-print, #ai-report-print * { visibility: visible; }
        #ai-report-print {
          position: absolute; inset-inline-start: 0; top: 0; width: 100%; border: 0; box-shadow: none;
          background: #FFFFFF;
        }
        #ai-report-print, #ai-report-print * {
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
      }`}</style>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/reports/ai" className="mb-1 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
            <DirectionalIcon><ArrowLeft className="h-4 w-4" aria-hidden /></DirectionalIcon>
            {t('aiReports.title', { defaultValue: 'AI Reports' })}
          </Link>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-brand/10 text-brand">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            {/* plaintext: an Arabic title in the English UI (or vice versa) keeps its
                own direction, so the period dates don't render visually swapped. */}
            <span className="min-w-0 break-words" style={{ unicodeBidi: 'plaintext' }}>{report.title}</span>
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
            <Badge variant="outline">{t(`aiReports.type.${report.type}`, { defaultValue: report.type })}</Badge>
            <StatusBadge status={report.status} />
            <span className="font-mono">{report.periodFrom} → {report.periodTo}</span>
            {report.model && (
              <span>{t('aiReports.view.modelLabel', { defaultValue: 'Model' })}: <span className="font-mono">{report.model}</span></span>
            )}
            {report.createdByName && (
              <span>{t('aiReports.view.byLabel', { defaultValue: 'Requested by' })}: {report.createdByName}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {report.contentMd && (
            <>
              {/* Clipboard gets the HUMAN text — figure markers are machine protocol. */}
              <CopyButton value={stripAiReportFigureMarkers(report.contentMd)} variant="secondary" subject={report.title} toast />
              <Button type="button" variant="secondary" onClick={() => window.print()}>
                <Printer className="me-1.5 h-4 w-4" aria-hidden />
                {t('aiReports.view.print', { defaultValue: 'Print' })}
              </Button>
            </>
          )}
          {canManage && report.status !== 'running' && (
            <Button type="button" variant="secondary" onClick={() => void onRegenerate()} loading={regenerate.isPending}>
              <RefreshCw className="me-1.5 h-4 w-4" aria-hidden />
              {t('aiReports.view.regenerate', { defaultValue: 'Regenerate' })}
            </Button>
          )}
          {canManage && (
            <Button type="button" variant="danger" onClick={() => void onDelete()} loading={del.isPending}>
              <Trash2 className="me-1.5 h-4 w-4" aria-hidden />
              {t('aiReports.view.delete', { defaultValue: 'Delete' })}
            </Button>
          )}
        </div>
      </header>

      <SnapshotKpis report={report} />

      {report.status === 'running' && (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <span className="relative flex h-10 w-10" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-30" />
            <span className="relative grid h-10 w-10 place-items-center rounded-full bg-brand/10 text-brand">
              <Sparkles className="h-5 w-5" />
            </span>
          </span>
          <p className="font-medium">{t('aiReports.view.generating', { defaultValue: 'The analyst is writing your report…' })}</p>
          <p className="text-sm text-fg-muted">{t('aiReports.view.generatingHint', { defaultValue: 'This usually takes under a minute. The page updates by itself.' })}</p>
          <div className="mt-2 w-full max-w-xl space-y-2 text-start">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </Card>
      )}

      {report.status === 'failed' && (
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8" aria-hidden />}
          title={t('aiReports.view.failed', { defaultValue: 'Generation failed' })}
          description={errorText(report.errorCode)}
          action={(
            <div className="flex flex-wrap justify-center gap-2">
              {canManage && (
                <Button type="button" onClick={() => void onRetry()} loading={regenerate.isPending}>
                  <RefreshCw className="me-1.5 h-4 w-4" aria-hidden />
                  {t('aiReports.view.retry', { defaultValue: 'Retry' })}
                </Button>
              )}
              {report.errorCode === 'ai_not_configured' && canAiAdmin && (
                <Button type="button" variant="secondary" onClick={() => navigate('/settings/ai')}>
                  {t('aiReports.notConfigured.cta', { defaultValue: 'Open AI settings' })}
                </Button>
              )}
            </div>
          )}
        />
      )}

      {report.status === 'succeeded' && report.contentMd && (
        <Card id="ai-report-print" className="p-6 sm:p-8" dir={report.language === 'ar' ? 'rtl' : 'ltr'}>
          <div className="mx-auto max-w-3xl">
            <div className="mb-6 hidden items-center justify-between border-b border-border pb-3 print:flex">
              <span className="flex items-center gap-2 font-semibold"><FileText className="h-4 w-4" aria-hidden />{report.title}</span>
              <span className="font-mono text-xs text-fg-muted">{report.periodFrom} → {report.periodTo}</span>
            </div>
            <ReportBody report={report} />
            {denied && (
              <p className="mt-8 border-t border-border pt-3 text-xs text-fg-muted">
                {t('aiReports.view.deniedNote', { defaultValue: 'Some sections were not included because your account cannot see them.' })}
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * The document: prose interleaved with real figures. The model drops
 * `[[chart:id]]` markers; each becomes the matching snapshot-rendered figure at
 * that spot (first occurrence wins). Whatever it didn't place lands in a
 * Figures appendix — so a report from a model that ignored the markers (or one
 * generated before figures existed) still gets every chart.
 */
function ReportBody({ report }: { report: AiReport }): JSX.Element {
  const { i18n } = useTranslation();
  // Figures are part of the document — their labels follow the REPORT's
  // language, not the staff UI's.
  const dt = i18n.getFixedT(report.language, 'aiReports');
  const ctx = { report, t: dt, locale: report.language };
  const figures = buildReportFigures(ctx);

  const segments: JSX.Element[] = [];
  const used = new Set<string>();
  let prose: string[] = [];
  const flush = (): void => {
    if (prose.length === 0) return;
    segments.push(<MarkdownLite key={`p${segments.length}`} text={prose.join('\n')} variant="document" />);
    prose = [];
  };
  for (const line of report.contentMd!.split('\n')) {
    const m = AI_REPORT_FIGURE_TOKEN.exec(line);
    const id = m?.[1] as AiReportFigureId | undefined;
    if (id && figures.has(id) && !used.has(id)) {
      used.add(id);
      flush();
      segments.push(<div key={`f${id}`}>{figures.get(id)}</div>);
    } else if (m) {
      // Unknown / duplicate marker — drop the line, never show raw protocol.
    } else {
      // Belt and braces: scrub any marker a model buried inside a prose line.
      prose.push(line.replace(/\[\[chart:[a-z_]+\]\]/gi, ''));
    }
  }
  flush();

  const leftover = [...figures.entries()].filter(([fid]) => !used.has(fid));
  return (
    <div>
      <style>{FIGURE_SCOPE_CSS}</style>
      {segments}
      {leftover.length > 0 && (
        <div className="mt-8 border-t border-border pt-2">
          <span role="heading" aria-level={2} className="mt-4 block text-xl font-bold">
            {dt('aiReports.figure.appendix', { defaultValue: 'Figures' })}
          </span>
          {leftover.map(([fid, node]) => <div key={fid}>{node}</div>)}
        </div>
      )}
    </div>
  );
}

// ---- deterministic snapshot widgets ----------------------------------------

interface SalesSection {
  seriesGranularity?: 'day' | 'month';
  seriesBaseCurrency?: Array<{ date: string; total: string }>;
  totalsByCurrency?: Array<{ currencyCode: string; documentCount: number; total: string }>;
}
interface PnlSection { netIncome?: string; baseCurrencyCode?: string }
interface AgingSection { totalsByCurrency?: Array<{ currencyCode: string; total: string }> }

function section<T>(report: AiReport, name: string): T | null {
  const s = report.snapshot.sections?.[name];
  if (!s || typeof s !== 'object' || (s as { denied?: boolean }).denied) return null;
  return s as T;
}

function SnapshotKpis({ report }: { report: AiReport }): JSX.Element | null {
  const { t, i18n } = useTranslation('aiReports');
  const base = report.snapshot.baseCurrencyCode;
  const money = (amount: string): string => formatMoney(amount, { locale: i18n.language, currency: base });

  const sales = section<SalesSection>(report, 'sales');
  const pnl = section<PnlSection>(report, 'profitAndLoss');
  const ar = section<AgingSection>(report, 'receivablesAging');
  const ap = section<AgingSection>(report, 'payablesAging');

  const cards: JSX.Element[] = [];
  const baseSales = sales?.totalsByCurrency?.find((x) => x.currencyCode === base);
  if (baseSales) {
    cards.push(<StatCard key="revenue" label={t('aiReports.view.kpi.revenue', { defaultValue: 'Revenue' })} value={money(baseSales.total)} icon={TrendingUp} tone="brand" hint={base} />);
  }
  // The document count sums across currencies (counts are currency-agnostic), so
  // it must not disappear just because no sale happened in the base currency.
  const docs = (sales?.totalsByCurrency ?? []).reduce((n, x) => n + (x.documentCount || 0), 0);
  if (sales && docs > 0) {
    cards.push(<StatCard key="docs" label={t('aiReports.view.kpi.documents', { defaultValue: 'Documents' })} value={formatNumber(docs, { locale: i18n.language })} icon={FileText} tone="info" />);
  }
  if (pnl?.netIncome != null) {
    cards.push(<StatCard key="net" label={t('aiReports.view.kpi.netIncome', { defaultValue: 'Net income' })} value={money(pnl.netIncome)} icon={Banknote} tone={Number(pnl.netIncome) >= 0 ? 'success' : 'danger'} hint={base} />);
  }
  const arBase = ar?.totalsByCurrency?.find((x) => x.currencyCode === base);
  if (arBase) {
    cards.push(<StatCard key="ar" label={t('aiReports.view.kpi.receivables', { defaultValue: 'Receivables' })} value={money(arBase.total)} icon={Wallet} tone="warning" hint={base} />);
  }
  const apBase = ap?.totalsByCurrency?.find((x) => x.currencyCode === base);
  if (apBase) {
    cards.push(<StatCard key="ap" label={t('aiReports.view.kpi.payables', { defaultValue: 'Payables' })} value={money(apBase.total)} icon={Landmark} tone="neutral" hint={base} />);
  }

  if (cards.length === 0) return null;
  return <div className="grid gap-3 print:hidden sm:grid-cols-2 lg:grid-cols-4">{cards}</div>;
}

